import { isValueless } from "@eudonet/opal";
const OP = "advancedFilterLibrary.operators";
const CONJ = "advancedFilterLibrary.conjunctions";
const VAL = "advancedFilterLibrary.values";
const MATCH = "advancedFilterLibrary.match";
const AGG = "advancedFilterLibrary.aggregate";
const EMPTY_KEY = "advancedFilterLibrary.tooltip.empty";
const UNSUPPORTED_KEY = "advancedFilterLibrary.tooltip.unsupportedCriterion";
function has(node, key) {
    return typeof node === "object" && node !== null && key in node;
}
function isGroup(node) {
    return has(node, "conditions") && Array.isArray(node.conditions);
}
function isRelationship(node) {
    return has(node, "path") && has(node, "where");
}
function isAggregate(node) {
    return has(node, "path") && has(node, "aggregate");
}
function isScalar(node) {
    return has(node, "field") && has(node, "op");
}
function formatValue(v, t) {
    if (typeof v === "boolean")
        return t(`${VAL}.${v ? "true" : "false"}`);
    if (typeof v === "number")
        return String(v);
    if (typeof v === "string")
        return `"${v}"`;
    // Defensive: a scalar reaching here always has a primitive; null/undefined/object
    // must never render as "undefined" / "[object Object]".
    return "";
}
function labelFor(path, fields) {
    return fields.find((f) => f.path === path)?.label ?? path;
}
function scalarToText(node, fields, t) {
    const label = labelFor(node.field, fields);
    const op = node.op;
    if (isValueless(op)) {
        return `${label} ${t(`${OP}.${op}`)}`;
    }
    if (op === "lastNDays" || op === "nextNDays") {
        return `${label} ${t(`${OP}.${op}`, { n: node.value })}`;
    }
    if (op === "between") {
        const [a, b] = node.values ?? [];
        return `${label} ${t(`${OP}.between`)} ${formatValue(a, t)} ${t(`${CONJ}.and`)} ${formatValue(b, t)}`;
    }
    if (op === "in" || op === "notIn") {
        const inner = (node.values ?? [])
            .map((v) => (typeof v === "boolean" ? t(`${VAL}.${v ? "true" : "false"}`) : String(v)))
            .join(", ");
        return `${label} ${t(`${OP}.${op}`)} (${inner})`;
    }
    return `${label} ${t(`${OP}.${op}`)} ${formatValue(node.value, t)}`;
}
function relationshipToText(node, fields, t) {
    const label = labelFor(node.path, fields);
    const match = t(`${MATCH}.${node.match}`);
    return `${label} (${match}) : ${nodeToText(node.where, fields, t)}`;
}
function aggregateToText(node, fields, t) {
    const agg = t(`${AGG}.${node.aggregate}`);
    const label = labelFor(node.path, fields);
    const fieldPart = node.field ? ` (${labelFor(node.field, fields)})` : "";
    const comparison = node.op ? ` ${t(`${OP}.${node.op}`)} ${formatValue(node.value, t)}` : "";
    return `${agg} ${label}${fieldPart}${comparison}`;
}
function groupToText(node, fields, t, nested) {
    const conj = t(`${CONJ}.${node.operator === "or" ? "or" : "and"}`);
    const parts = node.conditions.map((child) => nodeToText(child, fields, t, true));
    const joined = parts.join(` ${conj} `);
    return nested ? `(${joined})` : joined;
}
function nodeToText(node, fields, t, nested = false) {
    if (isGroup(node))
        return groupToText(node, fields, t, nested);
    if (isRelationship(node))
        return relationshipToText(node, fields, t);
    if (isAggregate(node))
        return aggregateToText(node, fields, t);
    if (isScalar(node))
        return scalarToText(node, fields, t);
    // Unknown node shape (a future OPQRY construct) — degrade gracefully, never emit garbage.
    return t(UNSUPPORTED_KEY);
}
/**
 * Renders a stored criteria AST into a human-readable restitution:
 * `{FieldLabel} {operator} {value(s)}` joined by ET/OU, nested groups parenthesised,
 * relationship-match and aggregate nodes spelled out. Field labels come from `fields`;
 * operator/conjunction/value/match/aggregate words come from i18n via `t`.
 */
export function criteriaToText(ast, fields, t) {
    if (isGroup(ast)) {
        if (ast.conditions.length === 0)
            return t(EMPTY_KEY);
        return groupToText(ast, fields, t, false);
    }
    return nodeToText(ast, fields, t);
}
